'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'
import { useConfig } from 'wagmi'
import { getPublicClient } from 'wagmi/actions'

import type { ChainId } from '../configs/chains'

import { useUserAccount } from '../providers/UserAccountProvider'
import { maybeGetAddress } from '../utils/account.utils'
import { fetchSafeWalletHealth, type SafeWalletHealth } from '../utils/safe-wallet.utils'

const RABBY_SAFE_WARNING_ID = 'rabby-safe-wallet-warning'
const warnedAddresses = new Set<string>()
const inFlightProbes = new Map<string, Promise<SafeWalletHealth | undefined>>()

/** Warns when a Rabby-connected address is a Safe. @public */
export function useRabbySafeWarning() {
  const { chainId: accountChainId, connector, isConnected, userAddress } = useUserAccount()
  const isRabby = isConnected && connector?.id === 'io.rabby'
  const address = maybeGetAddress(userAddress)
  const chainId = accountChainId as ChainId
  const config = useConfig()

  useEffect(() => {
    if (!isRabby || !address) return

    const warningKey = `${chainId}:${address}`
    if (warnedAddresses.has(warningKey)) return

    let cancelled = false
    let probe = inFlightProbes.get(warningKey)

    if (!probe) {
      probe = (async () => {
        try {
          const client = getPublicClient(config, { chainId })
          if (!client) return
          return await fetchSafeWalletHealth(client, address)
        } catch {
          return
        }
      })()
      inFlightProbes.set(warningKey, probe)
    }

    void probe
      .then(health => {
        if (!health || cancelled || warnedAddresses.has(warningKey)) return

        warnedAddresses.add(warningKey)
        if (health.isSafeWallet) {
          toast.warning(
            'Safe via Rabby is unsupported. Connect with the Safe app or WalletConnect.',
            { id: RABBY_SAFE_WARNING_ID }
          )
        }
      })
      .finally(() => {
        if (inFlightProbes.get(warningKey) === probe) {
          inFlightProbes.delete(warningKey)
        }
      })

    return () => {
      cancelled = true
    }
  }, [address, chainId, config, isRabby])
}
